import classNames from 'classnames'
import { FC, ReactNode } from 'react'

import { Translation } from 'cng-common/features/translations'
import { TReplaceOptions } from 'cng-common/features/translations/components/translation/models/replaceOptions'
import { ITooltipProps, Tooltip } from 'core-components/library/tooltip'

import { CockpitCollapsedMetric } from '../../../components/cockpitCollapsedMetric'

import styles from './CockpitMetricCell.module.scss'

export type MetricDeltaTone = 'positive' | 'negative' | 'neutral'

interface ICockpitMetricCellProps {
  label: string
  labelParams?: TReplaceOptions[]
  value: ReactNode
  /** Growth against the baseline. Omitted on the baseline cell itself. */
  delta?: string
  deltaTone?: MetricDeltaTone
  /** What the ⓘ tooltip beside the label draws, explaining how the value is derived. */
  hint?: ITooltipProps['tipContent']
  /** A second value stacked below the primary one, e.g. the SOB figure under the sales figure. */
  secondaryValue?: ReactNode
  secondaryDelta?: string
  secondaryDeltaTone?: MetricDeltaTone
}

const InfoIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="6.75" stroke="currentColor" strokeWidth="1.2" />
    <path d="M8 7.25v4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    <circle cx="8" cy="5.15" r="0.8" fill="currentColor" />
  </svg>
)

export const CockpitMetricCell: FC<ICockpitMetricCellProps> = ({
  label,
  labelParams,
  value,
  delta,
  deltaTone = 'neutral',
  hint,
  secondaryValue,
  secondaryDelta,
  secondaryDeltaTone = 'neutral',
}) => (
  <CockpitCollapsedMetric
    alignValueColumns
    label={
      <>
        {/* The ⓘ is balanced by an unseen twin on the left, so the label's words stay centred
            over the figures; a label without one has nothing beside it to balance. */}
        {hint && (
          <span className={classNames(styles.hint, styles.hidden)} aria-hidden="true">
            <InfoIcon />
          </span>
        )}
        <Translation text={label} replaceParams={labelParams} span />
        {hint && (
          <Tooltip tipContent={hint} inline>
            <span className={styles.hint}>
              <InfoIcon />
            </span>
          </Tooltip>
        )}
      </>
    }
    value={value}
    valueSuffix={
      delta ? (
        <span className={classNames(styles.delta, styles[`delta--${deltaTone}`])}>{delta}</span>
      ) : undefined
    }
    secondaryValue={secondaryValue}
    secondarySuffix={
      secondaryDelta ? (
        <span className={classNames(styles.secondaryDelta, styles[`delta--${secondaryDeltaTone}`])}>
          {secondaryDelta}
        </span>
      ) : undefined
    }
    reserveSecondarySpace
  />
)
